import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import { Close, SwapHoriz } from "@mui/icons-material";
import { hostname, parseError } from "../../../utils";
import axios from "axios";
import { Document } from "../../../types";
import LoadingButton from "../../../components/LoadingButton";

interface CompanyUser {
  id: string;
  uid: string | null;
  email: string;
  role: string;
  displayName?: string | null;
  photoURL?: string | null;
  hasLoggedIn?: boolean;
}

interface TransferFolderDialogProps {
  open: boolean;
  onClose: () => void;
  folder: Document | null;
  companyId: string;
  currentUserUid: string;
  getToken: () => Promise<string>;
  onTransferComplete?: () => void;
}

const TransferFolderDialog: React.FC<TransferFolderDialogProps> = ({
  open,
  onClose,
  folder,
  companyId,
  currentUserUid,
  getToken,
  onTransferComplete,
}) => {
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Fetch company users when dialog opens
  useEffect(() => {
    if (open && companyId) {
      fetchCompanyUsers();
    }
  }, [open, companyId]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedUserId("");
      setError("");
      setSuccess(false);
    }
  }, [open]);

  const fetchCompanyUsers = async () => {
    setLoadingUsers(true);
    setError("");
    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/company_users/${companyId}?per_page=50`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      // Filter out current user and users without a uid (haven't logged in yet)
      const users = (response.data.company_users || []).filter(
        (user: CompanyUser) => user.uid && user.uid !== currentUserUid
      );
      setCompanyUsers(users);
    } catch (err: any) {
      console.error("Error fetching company users:", err);
      setError("Failed to load team members");
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleTransfer = async () => {
    if (!folder || !selectedUserId) return;

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/folders/${folder.id}/transfer`,
        {
          companyId,
          targetUid: selectedUserId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSuccess(true);
      setTimeout(() => {
        onClose();
        if (onTransferComplete) {
          onTransferComplete();
        }
      }, 1500);
    } catch (err: any) {
      console.error("Error transferring folder:", err);
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  const selectedUser = companyUsers.find((u) => u.uid === selectedUserId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SwapHoriz color="primary" />
          Transfer Folder
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Folder transferred successfully!
          </Alert>
        ) : (
          <>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Transfer ownership of <strong>"{folder?.name}"</strong> to another
              team member. All documents and subfolders within this folder will
              also be transferred.
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              After transfer, the folder will appear in the recipient's
              Knowledge Base and will no longer be visible in yours.
            </Alert>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loadingUsers ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : companyUsers.length === 0 ? (
              <Alert severity="warning">
                No other team members available. Only users who have logged in
                can receive folder transfers.
              </Alert>
            ) : (
              <FormControl fullWidth>
                <InputLabel id="transfer-user-select-label">
                  Transfer to
                </InputLabel>
                <Select
                  labelId="transfer-user-select-label"
                  value={selectedUserId}
                  label="Transfer to"
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  disabled={loading}
                  renderValue={() => {
                    if (!selectedUser) return "";
                    return (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar
                          src={selectedUser.photoURL || undefined}
                          sx={{ width: 24, height: 24 }}
                        >
                          {(selectedUser.displayName || selectedUser.email)?.[0]?.toUpperCase()}
                        </Avatar>
                        <Typography>
                          {selectedUser.displayName || selectedUser.email}
                        </Typography>
                      </Box>
                    );
                  }}
                >
                  {companyUsers.map((user) => (
                    <MenuItem key={user.uid} value={user.uid || ""}>
                      <ListItemAvatar>
                        <Avatar
                          src={user.photoURL || undefined}
                          sx={{ width: 32, height: 32 }}
                        >
                          {(user.displayName || user.email)?.[0]?.toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={user.displayName || user.email}
                        secondary={
                          user.displayName ? user.email : `${user.role}`
                        }
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleTransfer}
          loading={loading}
          disabled={!selectedUserId || loading || success || companyUsers.length === 0}
        >
          Transfer
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default TransferFolderDialog;
